import React from "react";
import { getApiBaseUrl } from "../../config/appSettings";
import { parseDetailSections } from "./detailSectionsSchema";

function resolveMediaUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
  const base = getApiBaseUrl();
  return `${base}${raw.startsWith("/") ? raw : `/${raw}`}`;
}

function CheckIcon() {
  return (
    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
      ✓
    </span>
  );
}

function HeroPreview({ section, product }) {
  const title = section.title || product?.name || "Product";
  const image = resolveMediaUrl(product?.image);
  const features = Array.isArray(section.features) ? section.features.filter((f) => f?.title || f?.text) : [];

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="relative overflow-hidden rounded-2xl bg-slate-900">
            {section.imageTag ? (
              <span className="absolute left-3 top-3 rounded-md bg-amber-300 px-2 py-1 text-[10px] font-bold uppercase text-slate-900">
                {section.imageTag}
              </span>
            ) : null}
            {image ? (
              <img src={image} alt="" className="aspect-[4/5] w-full object-cover opacity-95" />
            ) : (
              <div className="flex aspect-[4/5] items-center justify-center text-sm text-slate-400">No image</div>
            )}
          </div>
          {section.ratingText ? (
            <p className="mt-3 text-center text-xs text-slate-500">★★★★★ {section.ratingText}</p>
          ) : null}
        </div>
        <div>
          {section.category ? (
            <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
              {section.category}
            </span>
          ) : null}
          <h2 className="mt-3 text-2xl font-bold text-slate-900">{title}</h2>
          {section.description ? (
            <p className="mt-3 text-sm leading-6 text-slate-600">{section.description}</p>
          ) : null}
          <div className="mt-5 space-y-3">
            {features.map((feature, index) => (
              <div key={`hero-f-${index}`} className="flex gap-3">
                <CheckIcon />
                <div>
                  <div className="text-sm font-semibold text-slate-900">{feature.title}</div>
                  {feature.text ? <p className="text-xs text-slate-500">{feature.text}</p> : null}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {section.primaryCta?.label ? (
              <span className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">
                {section.primaryCta.label}
              </span>
            ) : null}
            {section.secondaryCta?.label ? (
              <span className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800">
                {section.secondaryCta.label}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function UseCasesPreview({ section }) {
  const items = Array.isArray(section.items) ? section.items.filter(Boolean) : [];
  const image = resolveMediaUrl(section.image);

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-stone-50 p-5 sm:p-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          {image ? (
            <img src={image} alt="" className="aspect-[3/4] w-full rounded-2xl object-cover" />
          ) : (
            <div className="flex aspect-[3/4] items-center justify-center rounded-2xl bg-slate-200 text-sm text-slate-500">
              No image
            </div>
          )}
        </div>
        <div>
          {section.badge ? (
            <span className="inline-flex rounded-full bg-white px-3 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-100">
              {section.badge}
            </span>
          ) : null}
          <h2 className="mt-3 text-2xl font-bold text-slate-900">{section.title || "Use cases"}</h2>
          {section.description ? (
            <p className="mt-3 text-sm leading-6 text-slate-600">{section.description}</p>
          ) : null}
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {items.map((item, index) => (
              <div
                key={`uc-${index}`}
                className="flex items-start gap-2 rounded-full bg-white px-3 py-2 text-xs text-slate-700 shadow-sm"
              >
                <CheckIcon />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeatureSplitPreview({ section }) {
  const points = Array.isArray(section.points) ? section.points.filter((p) => p?.title || p?.text) : [];
  const image = resolveMediaUrl(section.image);
  const imageFirst = section.imagePosition !== "right";

  const imageBlock = (
    <div>
      {image ? (
        <img src={image} alt="" className="aspect-[3/4] w-full rounded-2xl object-cover" />
      ) : (
        <div className="flex aspect-[3/4] items-center justify-center rounded-2xl bg-slate-200 text-sm text-slate-500">
          No image
        </div>
      )}
    </div>
  );

  const cardBlock = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-bold text-slate-900">{section.cardTitle || "Highlights"}</h3>
      <div className="mt-2 h-0.5 w-16 rounded bg-emerald-400" />
      <div className="mt-4 space-y-4">
        {points.map((point, index) => (
          <div key={`fs-${index}`} className="flex gap-3">
            <CheckIcon />
            <div>
              <div className="text-sm font-semibold text-slate-900">{point.title}</div>
              {point.text ? <p className="text-xs leading-5 text-slate-500">{point.text}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-stone-50 p-5 sm:p-6">
      <div className="mb-6 text-center">
        {section.badge ? (
          <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-800">
            {section.badge}
          </span>
        ) : null}
        <h2 className="mt-3 text-2xl font-bold text-slate-900">{section.title || "Features"}</h2>
        {section.description ? (
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600">{section.description}</p>
        ) : null}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {imageFirst ? (
          <>
            {imageBlock}
            {cardBlock}
          </>
        ) : (
          <>
            {cardBlock}
            {imageBlock}
          </>
        )}
      </div>
    </section>
  );
}

function FeatureListPreview({ section }) {
  const points = Array.isArray(section.points) ? section.points.filter((p) => p?.title || p?.text) : [];
  const image = resolveMediaUrl(section.image);
  const imageFirst = section.imagePosition === "left";

  const listBlock = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-bold text-slate-900">{section.title || "Features"}</h3>
      <div className="mt-2 h-0.5 w-16 rounded bg-amber-300" />
      <div className="mt-4 space-y-4">
        {points.map((point, index) => (
          <div key={`fl-${index}`} className="flex gap-3">
            <CheckIcon />
            <div>
              <div className="text-sm font-semibold text-slate-900">{point.title}</div>
              {point.text ? <p className="text-xs leading-5 text-slate-500">{point.text}</p> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const imageBlock = (
    <div>
      {image ? (
        <img src={image} alt="" className="aspect-[3/4] w-full rounded-2xl object-cover" />
      ) : (
        <div className="flex aspect-[3/4] items-center justify-center rounded-2xl bg-slate-200 text-sm text-slate-500">
          No image
        </div>
      )}
    </div>
  );

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-stone-50 p-5 sm:p-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {imageFirst ? (
          <>
            {imageBlock}
            {listBlock}
          </>
        ) : (
          <>
            {listBlock}
            {imageBlock}
          </>
        )}
      </div>
    </section>
  );
}

function InfographicsPreview({ section }) {
  const images = Array.isArray(section.images) ? section.images.filter(Boolean) : [];

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-stone-50 p-5 sm:p-6">
      <div className="grid gap-4 lg:grid-cols-2">
        {images.map((image, index) => {
          const src = resolveMediaUrl(image);
          return (
            <div key={`ig-${index}`} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-900">
              {src ? (
                <img src={src} alt="" className="w-full object-cover" />
              ) : (
                <div className="flex aspect-video items-center justify-center text-sm text-slate-400">
                  Missing image
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TablePreview({ section }) {
  const headers = Array.isArray(section.headers) ? section.headers : [];
  const rows = Array.isArray(section.rows) ? section.rows : [];
  if (!headers.length) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
      {section.caption ? (
        <h2 className="mb-4 text-lg font-bold text-slate-900">{section.caption}</h2>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={`th-${index}`}
                  className="border border-slate-200 bg-slate-50 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`tr-${rowIndex}`}>
                {headers.map((_, colIndex) => (
                  <td key={`td-${rowIndex}-${colIndex}`} className="border border-slate-200 px-3 py-2 text-slate-700">
                    {Array.isArray(row) ? row[colIndex] || "" : ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** CMS preview renderer for structured detail sections. */
export function DetailSectionsPreview({ sectionsJson, product }) {
  const doc = parseDetailSections(sectionsJson);
  const sections = (doc.sections || []).filter((s) => s && s.enabled !== false);

  if (!sections.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
        No JSON sections configured yet.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {sections.map((section, index) => {
        const key = section.id || `preview-${index}`;
        if (section.type === "hero") return <HeroPreview key={key} section={section} product={product} />;
        if (section.type === "useCases") return <UseCasesPreview key={key} section={section} />;
        if (section.type === "featureSplit") return <FeatureSplitPreview key={key} section={section} />;
        if (section.type === "featureList") return <FeatureListPreview key={key} section={section} />;
        if (section.type === "infographics") return <InfographicsPreview key={key} section={section} />;
        if (section.type === "table") return <TablePreview key={key} section={section} />;
        return (
          <div key={key} className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
            Unsupported section type: {section.type}
          </div>
        );
      })}
    </div>
  );
}
